/**
 * GeoClockBanner — In-app suggestion banner for smart clock-in/out.
 *
 * Shown on the Home page when there's a pending geo-clock suggestion.
 * Slides down from the top with action buttons.
 * For long shifts, includes a duration warning.
 */

import { useState, useCallback } from 'react';
import type { GeoClockSuggestion } from '../hooks/useGeoHeartbeat';

interface GeoClockBannerProps {
  suggestion: GeoClockSuggestion;
  onAccept: (id: number, extra?: Record<string, string>) => Promise<boolean>;
  onDismiss: (id: number) => Promise<boolean>;
}

/** Threshold hours to show a "long shift" warning */
const LONG_SHIFT_WARNING_HOURS = 12;

export default function GeoClockBanner({ suggestion, onAccept, onDismiss }: GeoClockBannerProps) {
  const [responding, setResponding] = useState(false);

  const isClockIn = suggestion.type === 'clock_in';
  const isLongShift = suggestion.duration_hours !== null && suggestion.duration_hours > LONG_SHIFT_WARNING_HOURS;

  const handleAccept = useCallback(async () => {
    setResponding(true);
    await onAccept(suggestion.id);
    setResponding(false);
  }, [suggestion.id, onAccept]);

  const handleDismiss = useCallback(async () => {
    setResponding(true);
    await onDismiss(suggestion.id);
    setResponding(false);
  }, [suggestion.id, onDismiss]);

  const stationLabel = suggestion.station_name ?? 'the station';

  return (
    <div
      className="w-full rounded-xl border-2 overflow-hidden animate-[slideDown_0.3s_ease-out]"
      style={{
        borderColor: isClockIn ? 'rgba(34, 197, 94, 0.4)' : 'rgba(251, 191, 36, 0.4)',
        background: isClockIn
          ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%)'
          : 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(251, 191, 36, 0.05) 100%)',
      }}
    >
      <div className="px-4 py-3">
        {/* Header */}
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg" role="img" aria-label={isClockIn ? 'arrive' : 'leave'}>
            {isClockIn ? '\u{1F4CD}' : '\u{1F44B}'}
          </span>
          <span className="text-sm font-bold text-content-primary">{isClockIn ? 'Clock In?' : 'Clock Out?'}</span>
        </div>

        {/* Message */}
        <p className="text-[13px] text-content-secondary mb-2 leading-snug">
          {suggestion.message ??
            (isClockIn
              ? `You\u2019re at ${stationLabel}. Ready to start your shift?`
              : `Looks like you\u2019re leaving ${stationLabel}. Done for the shift?`)}
        </p>

        {/* Long shift warning */}
        {isLongShift && (
          <div className="mb-2 px-2.5 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/20">
            <span className="text-xs text-amber-400 font-semibold">
              {suggestion.duration_hours}+ hour shift — please confirm
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleAccept}
            disabled={responding}
            className={`flex-1 py-2.5 text-sm font-bold rounded-lg border-none cursor-pointer disabled:opacity-50 disabled:cursor-default transition-colors ${
              isClockIn ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-amber-500 text-gray-900 hover:bg-amber-600'
            }`}
          >
            {responding ? '...' : isClockIn ? 'Clock In' : 'Clock Out'}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            disabled={responding}
            className="flex-1 py-2.5 text-sm font-semibold rounded-lg border border-edge-default bg-surface-raised text-content-secondary cursor-pointer disabled:opacity-50 disabled:cursor-default hover:bg-surface-sunken"
          >
            Not Now
          </button>
        </div>
      </div>
    </div>
  );
}
